"use client";

import { type ReactNode, createContext, useRef, useContext, useEffect } from "react";
import { useStore } from "zustand";

import { type UserStore, createUserStore, defaultInitState, UserState } from "@/stores/userStore";

export type UserStoreApi = ReturnType<typeof createUserStore>;

export const UserStoreContext = createContext<UserStoreApi | undefined>(undefined);

export interface UserStoreProviderProps {
  children: ReactNode;
  initialData?: UserState;
}

export const UserStoreProvider = ({ children, initialData }: UserStoreProviderProps) => {
  const storeRef = useRef<UserStoreApi>(null);
  if (!storeRef.current) {
    storeRef.current = createUserStore(initialData ?? defaultInitState);
  }

  // used for cross tab updates
  useEffect(() => {
    const handler = () => {
      // refetch the user data
      window.location.reload();
    };

    const storageListener = (event: StorageEvent) => {
      if (event.key === "user-update") {
        handler();
      }
    };

    window.addEventListener("storage", storageListener);

    return () => {
      window.removeEventListener("storage", storageListener);
    };
  }, []);

  return <UserStoreContext.Provider value={storeRef.current}>{children}</UserStoreContext.Provider>;
};

export const useUserStore = <T,>(selector: (store: UserStore) => T): T => {
  const userStoreContext = useContext(UserStoreContext);

  if (!userStoreContext) {
    throw new Error(`useUserStore must be used within UserStoreProvider`);
  }

  return useStore(userStoreContext, selector);
};
